import { cn } from '@/shared/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import Calendar from '@shared/components/calendar/Calendar';
import { Button } from '@shared/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shared/components/common/Form';
import { Input, PasswordInput } from '@shared/components/common/Input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@shared/components/common/Select';
import { DATE_YMD_ISO, REGEX_VALIDATE_EMAIL } from '@shared/components/constants/common.constants';
import { LIST_GENDER_OPTIONS, LIST_WORK_FORM_OPTIONS, PREFECTURES, WORK_TYPE } from '@shared/components/constants/select.constant';
import { ROUTES } from '@shared/configs/constants';
import { useStore } from '@shared/hooks/useStore';
import yup from '@shared/services/yup';
import { ROLE } from '@shared/types/enums';
import { handleBeforeInputNumber, handleInputNumberWithMaxLength, handlePasteNumber, KataNumberReg } from '@shared/utils/common';
import { formatDateTime } from '@shared/utils/datetime';
import { fetchAddressFromZip } from '@shared/utils/yubinbango';
import { NotFoundPage } from '@user/pages';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RegistrationFrame from '../RegistrationFrame';
import { PASSWORD_REGEX } from '@/shared/configs/regex';

export default observer(function RegistrationStepTwo() {
    // hook
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const uuid = params.get('uuid') || null;

    // if (!uuid) {
    //     navigate(ROUTES.registrationStepOne.href);
    // }

    // store

    const { authStore: { getUserByUUID, registerUserInfo } } = useStore();

    // form
    const validateSchema = yup.object().shape({
        firstName: yup.string().trim().required('error.required'),
        lastName: yup.string().trim().required('error.required'),
        firstNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
        lastNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
        companyName: yup.string().trim().required('error.required'),
        companyNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
        customerCode: yup.string(),
        gender: yup.string().required('error.required'),
        password: yup.string().required('error.required').matches(
            new RegExp(PASSWORD_REGEX),
            'アルファベットの小文字・大文字・数字を含む8～20文字でご入力ください'
        ),
        passwordConfirm: yup.string().oneOf([yup.ref('password')], 'error.confirm_password_not_match').required('error.required'),
        email: yup
            .string()
            .required('error.required')
            .matches(REGEX_VALIDATE_EMAIL, 'form_error_validate.email'),
        pharmacistNumber: yup.string().optional(),
        pharmacistNumberConfirm: yup.string().optional()
            .oneOf([yup.ref('pharmacistNumber')], 'error.student_confirm_pharmacist_not_match_format_2'),
        phoneNumber: yup.string().required('error.required').typeError('error.number_positive'),
        workForm: yup.string().optional(),
        zipCode: yup.string().required('error.required').typeError('error.number_positive'),
        prefecture: yup.string().required('error.required'),
        address1: yup.string().required('error.required'),
        address2: yup.string().optional().nullable(),
        workType: yup.string().optional(),
        birthday: yup.string().required('error.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            firstName: '',
            lastName: '',
            firstNameFuri: '',
            lastNameFuri: '',
            companyName: '',
            companyNameFuri: '',
            customerCode: '',
            gender: '',
            password: '',
            passwordConfirm: '',
            email: '',
            pharmacistNumber: '',
            pharmacistNumberConfirm: '',
            phoneNumber: '',
            workForm: '',
            zipCode: '',
            prefecture: '',
            address1: '',
            address2: '',
            workType: '',
            birthday: ''
        }
    });

    const [isCheckUuidOk, setIsCheckUuidOk] = useState<boolean>(true);

    const [studentType, setStudentType] = useState<number | null>(null);

    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState<boolean>(false);

    // life cycle
    useEffect(() => {
        if (uuid) {
            getInitUserInfor(uuid);
        }
    }, [uuid]);

    // function
    const getInitUserInfor = async (uuid: string) => {
        const res: any = await flowResult(getUserByUUID({ uuid }));
        if (res) {
            setIsCheckUuidOk(true);
            if (res.roleIds && res.roleIds.length > 0 && res.roleIds.includes(ROLE.STD_INDIVIDUAL)) {
                setStudentType(ROLE.STD_INDIVIDUAL);
            } else if (res.roleIds && res.roleIds.length > 0 && res.roleIds.includes(ROLE.STD_GROUP)) {
                setStudentType(ROLE.STD_GROUP);
            }

            let { id, userId, status, ...other } = res;

            let validData = Object.fromEntries(
                Object.entries(other).filter(([k, v]) => v != null)
            );
            form.reset({
                ...validData
            });
        } else {
            setIsCheckUuidOk(false);
        }
    };

    const onBlurZipCode = async (e) => {
        const zip = e.target.value?.replace(/\D/g, '');

        if (zip) {
            const addr = await fetchAddressFromZip(zip);
            if (addr?.region) form.setValue('prefecture', addr?.region);
            if (addr?.locality) form.setValue('address1', `${addr?.locality}${addr?.street}`);
            if (addr?.extended) form.setValue('address2', addr?.extended || '');
        }
    };

    const onSubmit = async (data: FormData) => {
        // navigate(ROUTES.registrationStepThree.href);

        const clean = Object.fromEntries(
            Object.entries(data).map(([k, v]) => [k, v === undefined ? null : v])
        );

        const res = await flowResult(registerUserInfo({
            // uuid
            firstName: clean.firstName ? clean.firstName : '',
            lastName: clean.lastName ? clean.lastName : '',
            firstNameFuri: clean.firstNameFuri ? clean.firstNameFuri : '',
            lastNameFuri: clean.lastNameFuri ? clean.lastNameFuri : '',
            email: clean.email ? clean.email : '',
            gender: clean.gender ? Number(clean.gender) : '',
            password: clean.password ? clean.password : '',
            customerCode: clean.customerCode ? clean.customerCode : '',
            birthday: formatDateTime(data.birthday, DATE_YMD_ISO),
            pharmacistNumber: clean.pharmacistNumber ? clean.pharmacistNumber : '',
            workForm: clean.workForm ? clean.workForm : '',
            workType: clean.workType ? clean.workType : '',
            zipCode: clean.zipCode ? clean.zipCode : '',
            prefecture: clean.prefecture ? clean.prefecture : '',
            address1: clean.address1 ? clean.address1 : '',
            address2: clean.address2 ? clean.address2 : '',
            phoneNumber: clean.phoneNumber ? clean.phoneNumber : '',
            companyName: clean.companyName ? clean.companyName : '',
            companyNameFuri: clean.companyNameFuri ? clean.companyNameFuri : ''
        }));

        if (res) {
            navigate(ROUTES.registrationStepThree.href);
        }
    };

    if (!uuid || !isCheckUuidOk) {
        return <NotFoundPage />;
    }

    const onError = (error) => {
        console.error(error);
    };

    return (
        <RegistrationFrame currentStep={2}>
            <>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit, onError)} className='flex flex-col gap-8 w-full h-adr'>
                        <div className='bg-background-1 grid sm:grid-cols-2 grid-cols-1 md:gap-6 gap-4 rounded-[4px]'>
                            <FormField
                                control={form.control}
                                name='firstName'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className='' required>
                                            姓
                                        </FormLabel>
                                        <FormControl>
                                            <Input className='h-14 rounded-none bg-white' {...field} maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='lastName'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className='' required>
                                            名
                                        </FormLabel>
                                        <FormControl>
                                            <Input className='h-14 rounded-none bg-white' {...field} maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='firstNameFuri'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className='' required>
                                            姓(フリガナ)
                                        </FormLabel>
                                        <FormControl>
                                            <Input className='h-14 rounded-none bg-white' {...field} maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='lastNameFuri'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className='' required>
                                            名(フリガナ)
                                        </FormLabel>
                                        <FormControl>
                                            <Input className='h-14 rounded-none bg-white' {...field} name='last_furi' autoComplete='new-password' maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='gender'
                                render={({ field, fieldState: { error } }) => (
                                    <FormItem className=''>
                                        <FormLabel className='' required>
                                            性別
                                        </FormLabel>
                                        <FormControl>
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger className={cn('h-14 rounded-none bg-white', error && 'border-destructive')}>
                                                    <SelectValue></SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectGroup>
                                                        {
                                                            LIST_GENDER_OPTIONS.map(gender => (
                                                                <SelectItem key={gender.value} value={gender.value.toString()}>
                                                                    {gender.label}
                                                                </SelectItem>
                                                            ))
                                                        }
                                                    </SelectGroup>

                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='birthday'
                                render={({ field, fieldState: { error } }) => (
                                    <FormItem className=''>
                                        <FormLabel required>
                                            生年月日
                                        </FormLabel>
                                        <FormControl>
                                            <Calendar
                                                classButton={cn('h-14 min-w-0 bg-white w-full rounded-none', error && 'border-destructive')}
                                                mode='single'
                                                selected={field.value}
                                                placeholder=''
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={false}
                                                required
                                                disabled={[
                                                    {
                                                        after: new Date()
                                                    }
                                                ]}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='email'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel className='' required>
                                            メールアドレスの確認
                                        </FormLabel>
                                        <FormControl>
                                            <Input className='h-14 rounded-none bg-white' disabled {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='password'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel className='' required>
                                            ログインパスワード
                                        </FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                type={!showNewPassword ? 'password' : 'text'}
                                                className='h-14 rounded-none bg-white'
                                                autoComplete='off'
                                                onToggleIcon={() => setShowNewPassword(!showNewPassword)}
                                                {...field}
                                                onPaste={e => e.preventDefault()}
                                                onChange={(value) => {
                                                    field.onChange(value);
                                                    form.trigger(['password']);
                                                    if (form.getValues('passwordConfirm')?.length > 0) {
                                                        form.trigger('passwordConfirm');
                                                    }
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='passwordConfirm'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel className='' required>
                                            ログインパスワード確認
                                        </FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                type={!showConfirmNewPassword ? 'password' : 'text'}
                                                className='h-14 rounded-none bg-white'
                                                autoComplete='false'
                                                onPaste={e => e.preventDefault()}
                                                onToggleIcon={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                                {...field}
                                                onChange={(value) => {
                                                    field.onChange(value);
                                                    form.trigger(['password', 'passwordConfirm']);
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='customerCode'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel className=''>
                                            お客様番号
                                        </FormLabel>
                                        <FormControl>
                                            <Input className='h-14 rounded-none bg-white' {...field} maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='md:px-6 sm:px-4 px-3'>
                            <div className='h-[1px] w-full bg-[#D9D9D9]'></div>
                        </div>

                        <div className='bg-background-1 grid sm:grid-cols-2 grid-cols-1 md:gap-6 gap-4 rounded-[4px]'>
                            <FormField
                                control={form.control}
                                name='pharmacistNumber'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className=''>
                                            薬剤師名簿登録番号
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className='h-14 rounded-none bg-white'
                                                maxLength={50}
                                                onPaste={e => e.preventDefault()}
                                                onChange={(value) => {
                                                    field.onChange(value);
                                                    form.trigger(['pharmacistNumber']);
                                                    const confirmVal = form.getValues('pharmacistNumberConfirm') || '';
                                                    if (confirmVal.length > 0) {
                                                        form.trigger('pharmacistNumberConfirm');
                                                    }
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='pharmacistNumberConfirm'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className=''>
                                            （再）薬剤師名簿登録番号
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className='h-14 rounded-none bg-white'
                                                maxLength={50}
                                                onPaste={e => e.preventDefault()}
                                                onChange={(value) => {
                                                    field.onChange(value);
                                                    form.trigger(['pharmacistNumber', 'pharmacistNumberConfirm']);
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='workForm'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className=''>
                                            沖/外選択
                                        </FormLabel>
                                        <FormControl>
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger className='h-14 rounded-none bg-white'>
                                                    <SelectValue placeholder='選択して下さい'></SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectGroup>
                                                        {
                                                            LIST_WORK_FORM_OPTIONS.map(w => (
                                                                <SelectItem key={w.value} value={w.value.toString()}>
                                                                    {w.label}
                                                                </SelectItem>
                                                            ))
                                                        }
                                                    </SelectGroup>
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='workType'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className=''>
                                            勤務業態
                                        </FormLabel>
                                        <FormControl>
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger className='h-14 rounded-none bg-white'>
                                                    <SelectValue placeholder='選択して下さい'></SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectGroup>
                                                        {
                                                            Object.keys(WORK_TYPE).map(k => (
                                                                <SelectItem key={WORK_TYPE[k]} value={WORK_TYPE[k]}>
                                                                    {WORK_TYPE[k]}
                                                                </SelectItem>
                                                            ))
                                                        }
                                                    </SelectGroup>
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='companyName'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel className='' required>
                                            法人名
                                        </FormLabel>
                                        <FormControl>
                                            <Input className='h-14 rounded-none bg-white' disabled={studentType === ROLE.STD_GROUP} {...field} maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='companyNameFuri'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel className='' required>
                                            法人名（フリガナ）
                                        </FormLabel>
                                        <FormControl>
                                            <Input className='h-14 rounded-none bg-white' disabled={studentType === ROLE.STD_GROUP} {...field} maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='md:px-6 sm:px-4 px-3'>
                            <div className='h-[1px] w-full bg-[#D9D9D9]'></div>
                        </div>

                        <div className='bg-background-1 grid sm:grid-cols-2 grid-cols-1 md:gap-6 gap-4 rounded-[4px]'>
                            <FormField
                                control={form.control}
                                name='zipCode'
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className='' required>
                                            郵便番号
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                className='h-14 rounded-none bg-white'
                                                onInput={e => handleInputNumberWithMaxLength(e, 255)}
                                                maxLength={255}
                                                {...field}
                                                onBeforeInput={handleBeforeInputNumber}
                                                onPaste={handlePasteNumber}
                                                onBlur={onBlurZipCode}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='prefecture'
                                render={({ field, fieldState: { error } }) => (
                                    <FormItem className='relative'>
                                        <FormLabel className='' required>
                                            都道府県
                                        </FormLabel>
                                        <FormControl>
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger className={cn('h-14 rounded-none bg-white', error && 'border-destructive')}>
                                                    <SelectValue></SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectGroup>
                                                        {
                                                            PREFECTURES.map(p => (
                                                                <SelectItem key={p} value={p}>
                                                                    {p}
                                                                </SelectItem>
                                                            ))
                                                        }
                                                    </SelectGroup>
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='address1'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel className='' required>
                                            住所
                                        </FormLabel>
                                        <FormControl>
                                            <Input {...field} className='h-14 rounded-none bg-white' maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='address2'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormControl>
                                            <Input {...field} value={field.value || ''} className='h-14 rounded-none bg-white' maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='phoneNumber'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel className='' required>
                                            電話番号
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className='h-14 rounded-none bg-white'
                                                onBeforeInput={handleBeforeInputNumber}
                                                onPaste={handlePasteNumber}
                                                onInput={e => handleInputNumberWithMaxLength(e, 20)}
                                                maxLength={20}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='w-full flex justify-center'>
                            <Button type='submit' variant='default' className='rounded-[10px] w-full h-14'>登録</Button>
                        </div>
                    </form>
                </Form>
            </>
        </RegistrationFrame>
    );
});
